<script setup>
import { ref, computed, onMounted } from 'vue';
import { formatDate } from '../../utils/formatDate';
import { TRIAL_ONGOING, SUBSCRIPTION_CANCELLED } from '../../utils/constants';
import { default as t } from '../../utils/translationStrings';
import Dialog from '../Dialog';
import UpdateAddressForm from './UpdateAddressForm';
import UpdatePasswordForm from './UpdatePasswordForm';

// Data.
const showDialog = ref(false);
const statusIsCancelled = ref(false);
const editMode = ref(false);
const editedData = ref('password');

// Props.
const props = defineProps({
  accountSpecs: {
    type: Object,
    default: () => { },
  },
  daysLeft: {
    type: Number,
    default: 0,
  },
  shopUrl: {
    type: String,
    required: true,
  },
  subscriptionType: {
    type: String,
    required: true,
  },
  trialDuration: {
    type: Number,
    default: 30,
  },
  user: {
    type: Object,
    required: true,
    default: () => { },
  },
});

// Computed.
const productInformation = computed(() => {
  const baseInfo = {
    product: props.accountSpecs?.productName,
    basePrice: `${props.accountSpecs?.price}€`,
  };

  // When in trial period.
  if (props.accountSpecs.isTrial) {
    return {
      ...baseInfo,
      trialPeriodStart: formatDate(props.accountSpecs?.startDate?.date),
      trialPeriodEnd: formatDate(props.accountSpecs?.trialEndDate?.date),
    };
  }

  // When subscription was cancelled.
  if (props.subscriptionType === SUBSCRIPTION_CANCELLED) {
    return {
      ...baseInfo,
      subscriptionStart: formatDate(props.accountSpecs?.startDate?.date),
      subscriptionEnd: formatDate(props.accountSpecs.cancelledOn?.date),
    };
  }

  // Default: ongoing subscription?
  return {
    ...baseInfo,
    subscriptionStart: formatDate(props.accountSpecs?.startDate?.date),
    nextCancellationDate: formatDate(
      props.accountSpecs?.nextPossibleCancelledDate?.date,
    ),
  };
});

const userInformation = computed(() => {
  let baseInfo = {
    email: props.user?.email,
    password: '••••••',
  };

  if (!props.accountSpecs.isTrial) {
    if (props.user?.defaultBillingAddress?.phoneNumber) {
      baseInfo = {
        ...baseInfo,
        phoneNumber: props.user.defaultBillingAddress.phoneNumber,
      };
    }

    return {
      ...baseInfo,
      companyAddress: `
        ${props.user?.defaultBillingAddress?.company}<br>
        ${props.user?.defaultBillingAddress?.street}<br>
        ${props.user?.defaultBillingAddress?.zipcode} ${props.user?.defaultBillingAddress?.city}<br>
        ${props.user?.defaultBillingAddress?.country.name}`,
    };
  }

  return baseInfo;
});

// Decide which form to load based on data being edited.
const formComponent = computed(() => {
  return editedData.value === 'password'
    ? UpdatePasswordForm
    : UpdateAddressForm;
});

// Events.
const emit = defineEmits(['requestRefresh']);

// Methods.
const openForEdit = (type) => {
  editMode.value = true;
  editedData.value = type;
};

// Show confirmation dialog if the user is being redirected after cancellation.
onMounted(() => {
  const params = new URLSearchParams(document.location.search);

  if (params.get('subscription') === 'cancelled') {
    showDialog.value = true;
    statusIsCancelled.value = true;
  }
});

// Method to show the backup banner
const showBackupMessage = ref(false);
const showBackupBanner = () => {
  showBackupMessage.value = true;
};
</script>

<style>
.backup-banner {
  margin-top: 10px;
  padding: 15px;
  background-color: #60c3d9;
  border: 1px solid #add8e6;
  border-radius: 5px;
  font-size: 14px;
  color: #ffffff;
}

.button--border {
  margin-right: 1em;
}
</style>

<template>
  <div class="user-information">
    <!-- 'Trial/subscription info' section. -->
    <div class="dashboard-page__section dashboard-page__section--product">
      <h2>{{ t.dashboardPage.productInfo }}</h2>
      <div class="dashboard-page__information">
        <div v-for="info in Object.keys(productInformation)" class="dashboard-page__info-item"
          :class="`dashboard-page__info-item--${subscriptionType}`" :key="info">
          <!-- Labels. -->
          <div class="dashboard-page__label">
            {{ t.dashboardMyData[info] }}:
          </div>

          <!-- Values. -->
          <div class="dashboard-page__value" :class="{
            'dashboard-page__value--link': info === 'trialPeriodEnd',
          }">
            <p :class="{ 'value-info-end': info === 'trialPeriodEnd' }">
              {{ productInformation[info] }}
            </p>

            <!-- Additional info about pricing. -->
            <span v-if="info === 'basePrice'">
              <!-- When in trial. -->
              <template v-if="accountSpecs?.isTrial">
                {{
                  subscriptionType === TRIAL_ONGOING
                    ? t.dashboardMyData.priceTrialOngoing
                    : t.dashboardMyData.priceTrialEnded
                }}
              </template>

              <!-- When in subscription. -->
              <template v-else>
                {{ t.dashboardMyData.priceSubscription }}
              </template>
            </span>

            <!-- Additional info about end date. -->
            <div v-if="
              [
                'trialPeriodEnd',
                'nextCancellationDate',
                'subscriptionEnd',
              ].includes(info)
            " class="dashboard-page__value-info">
              <!-- When in trial. -->
              <template v-if="info === 'trialPeriodEnd'">
                <div class="messages messages--subscription-info" :class="{
                  'messages--trial': subscriptionType === TRIAL_ONGOING,
                  'messages--ended': subscriptionType !== TRIAL_ONGOING,
                }">
                  {{
                    subscriptionType === TRIAL_ONGOING
                      ? t.dashboardMyData.untilTrialEndMessage.replace('@days_left', daysLeft)
                      : t.dashboard.trialEndedMessage.replace('@trial_duration', trialDuration)
                  }}
                </div>
                <!-- Link to subscription URL (shop). -->
                <a :href="`${shopUrl}/checkout/real-product`" class="button button--border" target="_blank">
                  {{ t.dashboard.completeSubscription }}
                </a>

                <a v-if="subscriptionType === TRIAL_ONGOING" @click="showBackupBanner" class="button button--border" target="_blank">
                  Backup einrichten
                </a>
               <!-- New Button for Backup -->
                <!-- Backup Banner -->
                 <div v-if="showBackupMessage" class="backup-banner">
                  Ein Mitarbeiter meldet sich innerhalb der nächsten 24h per Mail bei dir und richtet dann individuell mit dir dein Backup ein!
                </div>
              </template>

              
             
              <!-- When in subscription. -->
              <template v-if="info === 'nextCancellationDate'">
                <div class="dashboard-page__action">
                  <!-- Cancel subscription dialog trigger. -->
                  <button @click="showDialog = true">
                    {{ t.dashboardMyData.cancelSubscription }}
                  </button>
                </div>
              </template>

              <!-- When subscription was cancelled. -->
              <template v-if="info === 'subscriptionEnd'">
                <!-- Special styling for cancellation message. -->
                <div class="dashboard-page__value-notice">
                  {{ t.dashboardMyData.subscriptionEndedMessage }}
                </div>

                <div class="dashboard-page__action">
                  <!-- Revoke cancellation button (turned to subscription button in BRAG2230001-346). -->
                  <a :href="`${shopUrl}/checkout/real-product`" target="_blank">
                    {{ t.dashboard.completeSubscription }}
                  </a>
                </div>
              </template>
            </div>
          </div>
        </div>
        <!-- Product upgrades, if any. -->
        <template v-if="accountSpecs?.upgrades?.length">
          <h3>{{ t.dashboardMyData.productUpgrades }}</h3>
          <div v-for="item in accountSpecs.upgrades" :key="item.label"
            class="dashboard-page__info-item dashboard-page__info-item--full">
            <div class="dashboard-page__label">{{ item.label }}:</div>
            <div class="dashboard-page__value">{{ item.price }}€</div>
          </div>
        </template>
      </div>
    </div>

    <!-- 'Your information' section. -->
    <div class="dashboard-page__section dashboard-page__section--user">
      <h2>{{ t.dashboardPage.yourInformation }}</h2>
      <div class="dashboard-page__information">
        <div v-for="info in Object.keys(userInformation)" :key="info" class="dashboard-page__info-item">
          <div class="dashboard-page__label">
            {{ t.dashboardMyData[info] }}:
          </div>
          <div class="dashboard-page__value">
            <div>
              <p v-html="userInformation[info]"></p>
            </div>
          </div>
          <div class="dashboard-page__action">
            <!-- Action links (for changing address/password). -->
            <button v-if="info !== 'email'" @click="openForEdit(info)">
              {{ `${t.dashboardMyData[info]} ${t.change.toLowerCase()}` }}
            </button>
          </div>
        </div>
        <!-- Item with top border and modifier class (payment method name). -->
        <div class="dashboard-page__info-item dashboard-page__info-item--extra">
          <div class="dashboard-page__label">
            {{ t.dashboardPayment.paymentMethod }}:
          </div>
          <div class="dashboard-page__value">
            <div>
              <i :class="`fa-brands fa-${accountSpecs?.payment?.name}`"></i>
              <p>{{ accountSpecs?.payment?.name }}</p>
            </div>
            <span v-if="accountSpecs?.nextPayment?.date">
              {{
                t.dashboardMyData.nextBillingDate.replace(
                  '@next_pay',
                  formatDate(accountSpecs.nextPayment.date),
                )
              }}
            </span>
          </div>
          <div class="dashboard-page__action">
            <RouterLink to="/payment">
              <span v-html="t.dashboardMyData.toThePaymentPage"></span>
            </RouterLink>
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog for confirming subscription cancellation. -->
    <Dialog v-if="showDialog" v-model="showDialog">
      <template #header>
        <h2>
          {{
            statusIsCancelled
              ? t.dashboardMyData.postCancellationDialogTitle
              : t.dashboardMyData.cancelDialogTitle
          }}
        </h2>
      </template>
      <template #content>
        <p>
          {{
            statusIsCancelled
              ? t.dashboardMyData.postCancellationDialogText
              : t.dashboardMyData.cancelDialogText
          }}
        </p>
      </template>
      <template #footer>
        <a v-if="!statusIsCancelled" class="button button--danger button--no-icon"
          :href="`${shopUrl}/account/mollie/subscriptions/${accountSpecs?.subscriptionId}/cancel`" target="_blank">
          {{ t.dashboardMyData.cancelNowButton }}
        </a>
        <button class="button button--border button--no-icon" @click="showDialog = false">
          {{
            statusIsCancelled
              ? t.dashboardMyData.closeButton
              : t.dashboardMyData.abortButton
          }}
        </button>
      </template>
    </Dialog>

    <!-- Dialog for password or address update forms. -->
    <Dialog v-if="editMode" v-model="editMode" hide-footer>
      <template #header>
        <h2>{{ t.dashboardMyData.updateDataDialogTitle }}</h2>
      </template>
      <template #content>
        <Component :is="formComponent" :user="user" @abort="editMode = false"
          @addressUpdated="emit('requestRefresh')" />
      </template>
    </Dialog>
  </div>
</template>
